import { useState, useEffect } from 'react'
import { DentalDetails } from '../../../../../types/FirestoreUser'

interface DentalFormStateProps {
  initialValues: Partial<DentalDetails>
}

/**
 * Hook for managing dental form state
 */
export default function useDentalFormState({
  initialValues,
}: DentalFormStateProps) {
  // Amalgam state
  const [currentNumAmalgamFillings, setCurrentNumAmalgamFillings] = useState(
    initialValues?.currentNumAmalgamFillings?.toString() || ''
  )
  const [amalgamTotalTeeth, setAmalgamTotalTeeth] = useState(
    initialValues?.totalNumAmalgamFillings?.toString() || ''
  )
  const [firstAmalgamAge, setFirstAmalgamAge] = useState(
    initialValues?.ageFirstAmalgam?.toString() || ''
  )
  const [lastAmalgamAge, setLastAmalgamAge] = useState(
    initialValues?.ageLastAmalgamRemoved?.toString() || ''
  )

  // Root Canal state
  const [currentRootCanals, setCurrentRootCanals] = useState(
    initialValues?.currentNumRootCanals?.toString() || ''
  )
  const [rootCanalsTotal, setRootCanalsTotal] = useState(
    initialValues?.totalNumRootCanals?.toString() || ''
  )
  const [firstRootCanalAge, setFirstRootCanalAge] = useState(
    initialValues?.ageFirstRootCanal?.toString() || ''
  )
  const [lastRootCanalAge, setLastRootCanalAge] = useState(
    initialValues?.ageLastRootCanalRemoved?.toString() || ''
  )

  // Extractions/Cavitations state
  const [removedTeeth, setRemovedTeeth] = useState(
    initialValues?.totalNumTeethRemoved?.toString() || ''
  )
  const [firstToothExtractedAge, setFirstToothExtractedAge] = useState(
    initialValues?.ageFirstToothRemoved?.toString() || ''
  )
  const [cavitChecked, setCavitChecked] = useState(
    initialValues?.checkedCavitations ? 'Yes' : 'No'
  )
  const [cavitations, setCavitations] = useState(
    initialValues?.numCavitations?.toString() || ''
  )

  // Implants state
  const [currentImplants, setCurrentImplants] = useState(
    initialValues?.currentNumImplants?.toString() || ''
  )
  const [implantTotalTeeth, setImplantTotalTeeth] = useState(
    initialValues?.totalNumImplants?.toString() || ''
  )
  const [firstDentalImplantAge, setFirstDentalImplantAge] = useState(
    initialValues?.ageFirstImplant?.toString() || ''
  )
  const [lastDentalImplantAge, setLastDentalImplantAge] = useState(
    initialValues?.ageLastImplantRemoved?.toString() || ''
  )

  const [isSaved, setIsSaved] = useState(initialValues?.isSaved || false)

  // Clear cavitations when cavitChecked is set to "No"
  useEffect(() => {
    if (cavitChecked === 'No') {
      setCavitations('')
    }
  }, [cavitChecked])

  // Prepare the dental details object for submission (only input fields, not calculated values)
  const prepareDentalDetails = (): Partial<DentalDetails> => {
    return {
      currentNumAmalgamFillings: currentNumAmalgamFillings
        ? Number(currentNumAmalgamFillings)
        : undefined,
      totalNumAmalgamFillings: amalgamTotalTeeth
        ? Number(amalgamTotalTeeth)
        : undefined,
      ageFirstAmalgam: firstAmalgamAge ? Number(firstAmalgamAge) : undefined,
      ageLastAmalgamRemoved: lastAmalgamAge
        ? Number(lastAmalgamAge)
        : undefined,

      currentNumRootCanals: currentRootCanals
        ? Number(currentRootCanals)
        : undefined,
      totalNumRootCanals: rootCanalsTotal ? Number(rootCanalsTotal) : undefined,
      ageFirstRootCanal: firstRootCanalAge
        ? Number(firstRootCanalAge)
        : undefined,
      ageLastRootCanalRemoved: lastRootCanalAge
        ? Number(lastRootCanalAge)
        : undefined,

      totalNumTeethRemoved: removedTeeth ? Number(removedTeeth) : undefined,
      ageFirstToothRemoved: firstToothExtractedAge
        ? Number(firstToothExtractedAge)
        : undefined,
      checkedCavitations: cavitChecked === 'Yes',
      numCavitations: cavitations ? Number(cavitations) : undefined,

      currentNumImplants: currentImplants ? Number(currentImplants) : undefined,
      totalNumImplants: implantTotalTeeth
        ? Number(implantTotalTeeth)
        : undefined,
      ageFirstImplant: firstDentalImplantAge
        ? Number(firstDentalImplantAge)
        : undefined,
      ageLastImplantRemoved: lastDentalImplantAge
        ? Number(lastDentalImplantAge)
        : undefined,
      isSaved,
    }
  }

  return {
    // Amalgam state
    currentNumAmalgamFillings,
    setCurrentNumAmalgamFillings,
    amalgamTotalTeeth,
    setAmalgamTotalTeeth,
    firstAmalgamAge,
    setFirstAmalgamAge,
    lastAmalgamAge,
    setLastAmalgamAge,

    // Root Canal state
    currentRootCanals,
    setCurrentRootCanals,
    rootCanalsTotal,
    setRootCanalsTotal,
    firstRootCanalAge,
    setFirstRootCanalAge,
    lastRootCanalAge,
    setLastRootCanalAge,

    // Extractions/Cavitations state
    removedTeeth,
    setRemovedTeeth,
    firstToothExtractedAge,
    setFirstToothExtractedAge,
    cavitChecked,
    setCavitChecked,
    cavitations,
    setCavitations,

    // Implants state
    currentImplants,
    setCurrentImplants,
    implantTotalTeeth,
    setImplantTotalTeeth,
    firstDentalImplantAge,
    setFirstDentalImplantAge,
    lastDentalImplantAge,
    setLastDentalImplantAge,

    // Save state
    isSaved,
    setIsSaved,

    // Helper function
    prepareDentalDetails,
  }
}
